"use client";

import { useState } from "react";
import { buildShareLinks, extractFirstParagraph, getBlogShareUrl, shareBlogWithImage } from "@/lib/blogShare";
import useShareImageFile from "./useShareImageFile";
import {
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
  FaFacebook,
  FaEnvelope,
  FaShareAlt,
  FaTimes,
  FaLink
} from "react-icons/fa";

// Mobile-only share button — desktop already has its own icon row rendered
// directly in BlogDetails.jsx; this adds a copy-link option desktop lacks.
const MobileShareButton = ({ selectedBlog }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Same canonical URL/links as the desktop icon row (src/lib/blogShare.js).
  const blogTitle = selectedBlog?.title || "Hachion Blog";
  const blogUrl = getBlogShareUrl(selectedBlog);
  const hrefs = buildShareLinks({
    url: blogUrl,
    title: blogTitle,
    html: selectedBlog?.description,
    metaDescription: selectedBlog?.meta_description,
  });
  const [copyFailed, setCopyFailed] = useState(false);
  const [shareError, setShareError] = useState(false);
  // Only set when this device's share sheet can take the blog's image file.
  const shareImageFile = useShareImageFile(selectedBlog);

  const handleShareWithImage = async () => {
    try {
      const description = selectedBlog?.meta_description || extractFirstParagraph(selectedBlog?.description, { maxLength: 200 });
      setShareError(false);
      await shareBlogWithImage({ file: shareImageFile, title: blogTitle, description, url: blogUrl });
    } catch {
      setShareError(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(blogUrl);
      setCopyFailed(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFailed(true);
    }
  };

  const open = (href, features = "noopener,noreferrer") => () => window.open(href, "_blank", features);
  const handleShare = {
    facebook: open(hrefs.facebook, "noopener,noreferrer,width=600,height=400"),
    twitter: open(hrefs.twitter, "noopener,noreferrer,width=600,height=400"),
    linkedin: open(hrefs.linkedin, "noopener,noreferrer,width=600,height=600"),
    whatsapp: open(hrefs.whatsapp),
    email: open(hrefs.email),
  };

  return (
    <>
      {/* Mobile Share Trigger Button - Only visible on mobile */}
      <div
        className="multimedia-option-open mobile-only"
        id="multimediaoptionOpen"
        role="button"
        tabIndex={0}
        aria-label="Share this blog"
        aria-expanded={isOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setIsOpen(!isOpen))}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FaShareAlt size={20} color="#fff" />
      </div>

      {/* Mobile Share Modal - Only visible on mobile when open */}
      {isOpen && (
        <div className="multimedia-option-container mobile-only">
          <div className="multimedia-option-header">
            <div className="cart-indicater">
              <img
                src="/Hachion-logo.png"
                alt="share"
                width="40"
                height="40"
              />
            </div>
            <div className="cart-heading">
              <h4>{blogTitle}</h4>
              <p>hachion.co</p>
            </div>
            <div
              className="close-cart"
              id="closeCart"
              onClick={() => setIsOpen(false)}
            >
              <FaTimes size={14} color="#666" />
            </div>
          </div>

          <div className="multimedia-option-body">
            <div className="share-multimedia-option">
              {/* WhatsApp */}
              <div
                onClick={handleShare.whatsapp}
                role="button"
                tabIndex={0}
                aria-label="Share on WhatsApp"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), handleShare.whatsapp())}
                className="circle-icon"
                style={{ cursor: 'pointer' }}
              >
                <div className="whatsapp circle">
                  <FaWhatsapp size={24} color="#fff" />
                </div>
                Whatsapp
              </div>

              {/* LinkedIn */}
              <div
                onClick={handleShare.linkedin}
                role="button"
                tabIndex={0}
                aria-label="Share on LinkedIn"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), handleShare.linkedin())}
                className="circle-icon"
                style={{ cursor: 'pointer' }}
              >
                <div className="linkedin circle">
                  <FaLinkedin size={24} color="#fff" />
                </div>
                Linkedin
              </div>

              {/* Twitter */}
              <div
                onClick={handleShare.twitter}
                role="button"
                tabIndex={0}
                aria-label="Share on X (Twitter)"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), handleShare.twitter())}
                className="circle-icon"
                style={{ cursor: 'pointer' }}
              >
                <div className="twitter circle">
                  <FaTwitter size={24} color="#fff" />
                </div>
                Twitter
              </div>

              {/* Facebook */}
              <div
                onClick={handleShare.facebook}
                role="button"
                tabIndex={0}
                aria-label="Share on Facebook"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), handleShare.facebook())}
                className="circle-icon"
                style={{ cursor: 'pointer' }}
              >
                <div className="facebook circle">
                  <FaFacebook size={24} color="#fff" />
                </div>
                Facebook
              </div>

              {/* Email */}
              <div
                onClick={handleShare.email}
                role="button"
                tabIndex={0}
                aria-label="Share by email"
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), handleShare.email())}
                className="circle-icon"
                style={{ cursor: 'pointer' }}
              >
                <div className="email circle">
                  <FaEnvelope size={24} color="#fff" />
                </div>
                Email
              </div>
            </div>

            {shareImageFile && (
              <div>
                <button className="link-button copy_link" onClick={handleShareWithImage}>
                  <span className="copy_text">{shareError ? "Could not open share sheet" : "Share with image"}</span>
                  <FaShareAlt size={16} color="currentColor" />
                </button>
              </div>
            )}

            <div>
              <button
                className="link-button copy_link"
                onClick={handleCopyLink}
              >
                <span className="copy_text">{copied ? 'Copied!' : copyFailed ? 'Copy failed - copy from address bar' : 'Copy Link!'}</span>
                <FaLink size={16} color="currentColor" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileShareButton;
